import { useState } from 'react'
import { useController } from 'react-hook-form'
import {
  Button,
  Stack,
  Typography,
  FormLabel,
  FormControl,
} from '@mui/material'
import useOverlay from '@/hooks/useOverlay'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import TagChip from '@/components/tags/TagChip'
import TagSelectMenu from '@/components/tags/TagSelectMenu'
import { PropertyFieldProps } from './PropertyField'

type PropertyMultiTagSelectProps = PropertyFieldProps

export default function PropertyMultiTagSelect({
  propertyId,
  property,
  required,
}: PropertyMultiTagSelectProps) {
  const { project } = useProjectContext()
  const overlay = useOverlay()
  const { field } = useController({ name: propertyId, rules: { required } })
  const [searchText, setSearchText] = useState('')

  const { label, description } = property.display
  const tagIds = field.value || []
  const selectedTags =
    project.tags?.filter((tag) => tagIds.includes(tag.id)) || []

  return (
    <FormControl sx={{ rowGap: 1 }}>
      <Stack>
        <FormLabel>{label}</FormLabel>
        {description && (
          <Typography variant="caption" color="text.secondary">
            {description}
          </Typography>
        )}
      </Stack>

      <Button
        variant="text"
        onClick={overlay.open}
        sx={{ ml: -0.5, width: 'fit-content' }}
      >
        Select Tags
      </Button>
      <Stack direction="row" gap={1} flexWrap="wrap">
        {selectedTags.map((tag) => (
          <TagChip
            tag={tag}
            key={tag.id}
            onDelete={() => {
              const values = tagIds.filter((item: string) => item !== tag.id)
              field.onChange(values)
            }}
          />
        ))}
      </Stack>

      <TagSelectMenu
        overlay={overlay}
        selectedProjectTags={selectedTags}
        onTagClick={(tag) => {
          const isSelected = tagIds.includes(tag.id)
          const values = isSelected
            ? tagIds.filter((item: string) => item !== tag.id)
            : [...tagIds, tag.id]

          field.onChange(values)
        }}
        searchText={searchText}
        setSearchText={setSearchText}
      />
    </FormControl>
  )
}
